import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { useRouterActions } from 'react-resource-router';

import Button from '@atlaskit/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';

import { navigationBlockerDefaultMessages } from './messages';

interface NavigationBlockerProps {
	shouldModalBeShown: () => boolean;
	customModalText?: typeof navigationBlockerDefaultMessages;
}

export const NavigationBlocker = (props: NavigationBlockerProps) => {
	const { formatMessage } = useIntl();
	const { registerBlock } = useRouterActions();
	const { shouldModalBeShown, customModalText } = props;
	const promiseInfo = useRef<{
		resolve: (answer: boolean) => void;
	}>({
		resolve: () => {},
	});
	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		const unregister = registerBlock(() => {
			/*
			 * This will run whenever the user tries to navigate away. This will show the styled modal.
			 * */
			if (shouldModalBeShown()) {
				return new Promise((resolve) => {
					/*
					 * Note that using useState instead of useRef to store the promise
					 * does not work. Not really sure why.
					 * */
					promiseInfo.current = {
						resolve,
					};
					setShowModal(true);
				});
			} else {
				return true;
			}
		});

		return () => {
			unregister();
		};
	}, [registerBlock, shouldModalBeShown]);

	useEffect(() => {
		/*
		 * The beforeunload listener will trigger when the user tries to close the browser tab or refresh the page. The styled modal won't work for this.
		 * */
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (shouldModalBeShown()) {
				event.preventDefault();
				event.returnValue = true;
			} else {
				return undefined;
			}
		};
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		window.addEventListener('beforeunload', handleBeforeUnload);
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		return (): void => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [shouldModalBeShown]);

	let title = formatMessage(navigationBlockerDefaultMessages.navigationBlockModalTitle);
	let body = formatMessage(navigationBlockerDefaultMessages.navigationBlockModalBody);
	let goBackButton = formatMessage(navigationBlockerDefaultMessages.navigationBlockGoBackButton);
	let continueButton = formatMessage(
		navigationBlockerDefaultMessages.navigationBlockContinueButton,
	);

	if (customModalText) {
		title = formatMessage(customModalText.navigationBlockModalTitle);
		body = formatMessage(customModalText.navigationBlockModalBody);
		goBackButton = formatMessage(customModalText.navigationBlockGoBackButton);
		continueButton = formatMessage(customModalText.navigationBlockContinueButton);
	}

	return showModal ? (
		<Modal
			onClose={() => {
				setShowModal(false);
				promiseInfo.current.resolve(false);
			}}
		>
			<ModalHeader>
				<ModalTitle appearance={'warning'}>{title}</ModalTitle>
			</ModalHeader>
			<ModalBody>{body}</ModalBody>
			<ModalFooter>
				<Button
					appearance="subtle"
					autoFocus
					onClick={() => {
						setShowModal(false);
						promiseInfo.current.resolve(false);
					}}
				>
					{goBackButton}
				</Button>
				<Button
					appearance="warning"
					onClick={() => {
						setShowModal(false);
						promiseInfo.current.resolve(true);
					}}
				>
					{continueButton}
				</Button>
			</ModalFooter>
		</Modal>
	) : null;
};
